import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { useEffect } from "react";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { Spinner } from "@/components/General/Spinner/spinner.component";

import Calculation from "./calculation/calculation.component";
import { useEPDMCalculation } from "./epdmCalculator";
import Result from "./result/result.component";

type Props = {
    isolationSkus: string;
    epdmSkus: string;
    surfaceGlueSku: string;
    borderGlueSku: string;
    overlapGlueSku: string;
};

const EPDMCalculator = ({
    isolationSkus = "",
    epdmSkus = "",
    surfaceGlueSku: SURFACE_GLUE_SKU = "",
    borderGlueSku: BORDER_GLUE_SKU = "",
    overlapGlueSku: OVERLAP_GLUE_SKU = ""
}: Props) => {
    const ISOLATION_SKUS = isolationSkus
        .split(",")
        .map((s) => s.replace(" ", ""));
    const EPDM_SKUS = epdmSkus.split(",").map((s) => s.replace(" ", ""));

    const {
        setRoofShape,
        setRoofShapeProperties,
        rectangularAmountEPDM,
        extraAmountEPDM,
        roofShape,
        roofShapeProperties,
        shoppingList,
        setProductData,
        setIsolationProduct,
        isolationProduct,
        isolationRequired,
        setIsolationRequired,
        hasCalculationValues
    } = useEPDMCalculation({
        BORDER_GLUE_SKU,
        EPDM_SKUS,
        OVERLAP_GLUE_SKU,
        SURFACE_GLUE_SKU
    });

    const [{ data, loading }, getProducts] = useBFFProductServiceApi<
        any,
        any,
        Array<Product<ProductFieldSet.ProductTile>>
    >(
        {
            params: {
                sku: [
                    ...EPDM_SKUS,
                    ...ISOLATION_SKUS,
                    SURFACE_GLUE_SKU,
                    BORDER_GLUE_SKU,
                    OVERLAP_GLUE_SKU
                ].join(","),
                fields: ProductFieldSet.ProductTile
            },
            url: `/products`,
            method: "GET"
        },
        { autoCancel: false, manual: true, useCache: false }
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        getProducts();
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        setProductData(data || []);
    }, [data]);

    const isolationProducts = (data || [])
        .filter((p) => !!ISOLATION_SKUS.find((s) => s === p.sku))
        .sort((a, b) => {
            return (
                ISOLATION_SKUS.indexOf(a.sku) - ISOLATION_SKUS.indexOf(b.sku)
            );
        });

    return (
        <div id="calculator">
            {loading && <Spinner />}
            {!loading && (
                <>
                    <Calculation
                        setRoofShapeProperties={setRoofShapeProperties}
                        roofShapeProperties={roofShapeProperties}
                        roofShape={roofShape}
                        isolationProducts={isolationProducts}
                        setRoofShape={setRoofShape}
                        setIsolationProduct={setIsolationProduct}
                        isolationProduct={isolationProduct}
                        isolationRequired={isolationRequired}
                        setIsolationRequired={setIsolationRequired}
                    />
                    {hasCalculationValues && (
                        <Result
                            rectangularAmountEPDM={rectangularAmountEPDM}
                            extraAmountEPDM={extraAmountEPDM}
                            shoppingList={shoppingList}
                            roofShape={roofShape}
                            isolationRequired={isolationRequired}
                            mailParams={{ roofShape, ...roofShapeProperties }}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export { EPDMCalculator };
